import React, { useEffect, useRef, useState } from "react";
import { resolvePhotoUrl } from "@/lib/api";

// The invoice is designed at a fixed reference width — the same layout the
// PDF has. On any screen narrower than that (mainly mobile), the whole
// thing scales down as one unit instead of reflowing, so what you see is
// always proportional to the real download, never a broken/wrapped table.
const DESIGN_W = 680;

const CURRENCY_PREFIX = {
  INR: "Rs. ", USD: "$", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$", AED: "AED ", SGD: "S$",
};

const money = (amount, currency) => {
  const prefix = CURRENCY_PREFIX[currency] || `${currency || ""} `;
  return `${prefix}${(Number(amount) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_LABELS = { draft: "DRAFT", sent: "SENT", paid: "PAID", overdue: "OVERDUE" };

/**
 * Print-style invoice preview with a member-selected professional accent.
 * Mirrors backend/invoice_pdf.py's layout so
 * what the user sees here matches the downloaded PDF.
 */
export default function InvoicePreview({ form, totals, invoiceNumber }) {
  const { business, customer, items, currency } = form;
  const status = STATUS_LABELS[form.status] || "DRAFT";
  const logoUrl = resolvePhotoUrl(business.logo_url);
  const sigUrl = resolvePhotoUrl(form.signature_url);
  const qrUrl = resolvePhotoUrl(form.qr_image_url);
  const accent = /^#[0-9a-f]{6}$/i.test(form.accent_color || "") ? form.accent_color : "#7F00FF";

  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [naturalHeight, setNaturalHeight] = useState(0);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const update = () => {
      const containerW = outer.offsetWidth;
      setScale(containerW > 0 ? Math.min(1, containerW / DESIGN_W) : 1);
      setNaturalHeight(inner.offsetHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [form, items]);

  return (
    <div ref={outerRef} className="min-w-0" style={{ height: naturalHeight * scale || undefined }}>
      <div
        ref={innerRef}
        data-testid="invoice-preview"
        className="bg-white text-[#111] rounded-2xl shadow-2xl overflow-hidden"
        style={{ fontFamily: "Inter, Helvetica, Arial, sans-serif", width: DESIGN_W, transform: `scale(${scale})`, transformOrigin: "top left" }}
      >
      <div className="p-8 sm:p-10">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-6 pb-6 border-b-2" style={{ borderColor: accent }}>
          <div className="flex items-start gap-3 min-w-0">
            {logoUrl && <img src={logoUrl} alt="Logo" className="h-14 w-14 object-contain rounded" />}
            <div className="min-w-0">
              <div className="text-lg font-bold truncate">{business.name || "Your Business"}</div>
              {business.address && <div className="text-xs text-gray-500 mt-1">{business.address}</div>}
              <div className="text-xs text-gray-500 mt-0.5">
                {[business.phone, business.email].filter(Boolean).join("  ·  ")}
              </div>
              {(business.gst_number || business.website) && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {[business.gst_number && `GSTIN: ${business.gst_number}`, business.website].filter(Boolean).join("  ·  ")}
                </div>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-3xl font-extrabold tracking-tight" style={{ color: accent }}>INVOICE</div>
            <div className="text-xs text-gray-500 mt-2 space-y-0.5">
              <div>Invoice #  <span className="text-gray-700 font-medium">{invoiceNumber || "-"}</span></div>
              <div>Date  <span className="text-gray-700 font-medium">{fmtDate(form.invoice_date)}</span></div>
              <div>Due  <span className="text-gray-700 font-medium">{fmtDate(form.due_date)}</span></div>
            </div>
            {form.show_status_badge && (
              <div className="inline-block mt-2 border rounded px-2.5 py-0.5 text-[10px] font-bold tracking-wide" style={{ borderColor: accent, color: accent }}>
                {status}
              </div>
            )}
          </div>
        </div>

        {/* BILL TO / PROJECT */}
        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <div>
            <div className="text-[10px] font-bold tracking-wide" style={{ color: accent }}>BILL TO</div>
            <div className="mt-1.5 text-sm font-semibold">{customer.name || "Customer"}</div>
            {customer.company_name && <div className="text-sm text-gray-600">{customer.company_name}</div>}
            {customer.address && <div className="text-sm text-gray-600 whitespace-pre-line">{customer.address}</div>}
            <div className="text-sm text-gray-600">{[customer.phone, customer.email].filter(Boolean).join("  ·  ")}</div>
            {customer.gst_number && <div className="text-sm text-gray-600">GSTIN: {customer.gst_number}</div>}
          </div>
          {(form.project_name || form.event_date || form.event_location || form.payment_terms) && (
            <div>
              <div className="text-[10px] font-bold tracking-wide" style={{ color: accent }}>PROJECT</div>
              {form.project_name && <div className="mt-1.5 text-sm font-semibold">{form.project_name}</div>}
              {form.event_date && <div className="text-sm text-gray-600">Event date: {fmtDate(form.event_date)}</div>}
              {form.event_location && <div className="text-sm text-gray-600">{form.event_location}</div>}
              {form.payment_terms && <div className="text-sm text-gray-600">Payment terms: {form.payment_terms}</div>}
            </div>
          )}
        </div>

        {/* ITEMS */}
        <table className="w-full mt-8 text-sm">
          <thead>
            <tr className="text-[10px] font-bold tracking-wide border-b-2" style={{ color: accent, borderColor: accent }}>
              <th className="text-left pb-2">DESCRIPTION</th>
              <th className="text-right pb-2 w-16">QTY</th>
              <th className="text-right pb-2 w-24">RATE</th>
              <th className="text-right pb-2 w-24">DISCOUNT</th>
              <th className="text-right pb-2 w-16">TAX %</th>
              <th className="text-right pb-2 w-28">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-gray-300 text-xs">No line items yet</td></tr>
            )}
            {items.map((item, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="py-2.5 pr-2">{item.description || "-"}</td>
                <td className="py-2.5 text-right tabular-nums">{item.quantity || 0}</td>
                <td className="py-2.5 text-right tabular-nums">{money(item.unit_price, currency)}</td>
                <td className="py-2.5 text-right tabular-nums">{money(item.discount, currency)}</td>
                <td className="py-2.5 text-right tabular-nums">{item.tax_percent || 0}%</td>
                <td className="py-2.5 text-right tabular-nums font-medium">{money(item.amount, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* TOTALS */}
        <div className="flex justify-end mt-6">
          <div className="w-full max-w-[240px] text-sm space-y-1.5">
            <Row label="Subtotal" value={money(totals.subtotal, currency)} />
            <Row label="Discount" value={`- ${money(totals.discount_total, currency)}`} />
            <Row label="Tax" value={money(totals.tax_total, currency)} />
            <div className="border-t-2 pt-1.5 mt-1.5" style={{ borderColor: accent }}>
              <Row label="Grand Total" value={money(totals.grand_total, currency)} bold big color={accent} />
              <Row label="Paid" value={money(totals.paid_amount, currency)} bold />
              <Row label="Balance Due" value={money(totals.balance_due, currency)} bold />
            </div>
          </div>
        </div>

        {/* NOTES / TERMS */}
        {form.notes && (
          <div className="mt-8">
            <div className="text-[10px] font-bold tracking-wide" style={{ color: accent }}>NOTES</div>
            <p className="mt-1.5 text-sm text-gray-600 whitespace-pre-line">{form.notes}</p>
          </div>
        )}
        {form.terms && (
          <div className="mt-5">
            <div className="text-[10px] font-bold tracking-wide" style={{ color: accent }}>TERMS &amp; CONDITIONS</div>
            <p className="mt-1.5 text-sm text-gray-600 whitespace-pre-line">{form.terms}</p>
          </div>
        )}

        {/* BANK / SIGNATURE */}
        {(form.bank_details?.account_name || form.bank_details?.account_number || form.upi_id) && (
          <div className="mt-5">
            <div className="text-[10px] font-bold tracking-wide" style={{ color: accent }}>PAYMENT DETAILS</div>
            <p className="mt-1.5 text-sm text-gray-600">
              {form.bank_details?.account_name}
              {form.bank_details?.account_name && <br />}
              {[form.bank_details?.account_number, form.bank_details?.ifsc, form.bank_details?.bank_name].filter(Boolean).join("  ·  ")}
              {form.upi_id && <><br />UPI: {form.upi_id}</>}
            </p>
          </div>
        )}

        <div className="flex items-end justify-between mt-10 pt-6">
          <div>
            {qrUrl && (
              <>
                <img src={qrUrl} alt="Payment QR" className="h-16 w-16 object-contain" />
                <div className="text-[10px] text-gray-400 mt-1">Scan to pay</div>
              </>
            )}
          </div>
          <div className="text-right">
            {sigUrl && <img src={sigUrl} alt="Signature" className="h-10 ml-auto object-contain" />}
            <div className="w-32 border-t border-gray-300 mt-2 pt-1.5 text-xs text-gray-500">Authorized Signature</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, big, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-semibold" : "text-gray-500"} style={color ? { color } : undefined}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-semibold" : ""} ${big ? "text-base" : ""}`} style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}
